import type { RJSFSchema } from '@rjsf/utils';

import { ROOM_RATE_FLASH_TYPE } from '~/consts/ratePlans';

export const DEFAULT_FLASH_TAX_FEE_PRODUCT_TYPE = ROOM_RATE_FLASH_TYPE;

type TaxRule = {
  amount?: number;
  currency?: string;
  name: string;
  rate?: number;
};

export type { TaxFeeFormItem };

export type TaxBreakdownRow = TaxRule & { location: string };

type TaxFeeFormItem = {
  name: string;
  unit: {
    amount?: number;
    currency?: string;
    value?: number;
  };
  type: string;
  per_person: boolean;
  product_type: string;
  payable_at_property: boolean;
  additional_tax: boolean;
  exclude_from_cm_rate: boolean;
  exclude_flash_pay_at_property: boolean;
};

const TAX_RULES_BY_COUNTRY: Record<string, Array<TaxRule>> = {
  fiji: [
    { name: 'VAT', rate: 12.5 },
    { name: 'TST', rate: 5 },
  ],
  maldives: [
    { name: 'GST', rate: 17 },
    { name: 'Service Fee', rate: 10 },
  ],
  indonesia: [
    { name: 'Gov Tax', rate: 10 },
    { name: 'Service Fee', rate: 11 },
  ],
  thailand: [
    { name: 'VAT', rate: 7 },
    { name: 'Service Charge', rate: 10 },
  ],
  mexico: [
    { name: 'VAT', rate: 16 },
    { name: 'Occupancy', rate: 3 },
    { name: 'Service fee', rate: 15 },
  ],
  'united kingdom': [{ name: 'VAT', rate: 15 }],
  vanuatu: [{ name: 'VAT', rate: 15 }],
  vietnam: [
    { name: 'VAT', rate: 10 },
    { name: 'Service Charge', rate: 5 },
  ],
  malaysia: [{ amount: 10, currency: 'MYR', name: 'Tourism Fee' }],
};

const CITY_RULE_COUNTRY: Record<string, string> = { dubai: 'united arab emirates' };

const TAX_RULES_BY_CITY: Record<string, Array<TaxRule>> = {
  dubai: [
    { name: 'Municipality Fee', rate: 7 },
    { name: 'Service Charge', rate: 10 },
    { name: 'VAT', rate: 5 },
    { amount: 20, currency: 'AED', name: 'Tourism Fee' },
  ],
};

const KOH_SAMUI_RULES: Array<TaxRule> = [...TAX_RULES_BY_COUNTRY.thailand, { name: 'Local/city tax', rate: 1 }];

function capitalize(value: string) {
  return value.replace(/\b\w/g, (char) => char.toUpperCase());
}

export const TAX_BREAKDOWN_TABLE: Array<TaxBreakdownRow> = [
  ...Object.entries(TAX_RULES_BY_COUNTRY).flatMap(([country, rules]) =>
    rules.map((rule) => ({ ...rule, location: capitalize(country) })),
  ),
  ...Object.entries(TAX_RULES_BY_CITY).flatMap(([city, rules]) =>
    rules.map((rule) => ({ ...rule, location: capitalize(city) })),
  ),
  ...KOH_SAMUI_RULES.map((rule) => ({ ...rule, location: 'Koh Samui, Thailand' })),
];

function normalize(value?: string | null) {
  return value?.trim().toLowerCase() ?? '';
}

function toTaxFeeFormItem(rule: TaxRule): TaxFeeFormItem {
  return {
    name: rule.name,
    unit:
      typeof rule.amount === 'number'
        ? {
            amount: rule.amount,
            currency: rule.currency,
          }
        : {
            value: rule.rate,
          },
    type: 'night',
    per_person: false,
    product_type: DEFAULT_FLASH_TAX_FEE_PRODUCT_TYPE,
    payable_at_property: false,
    additional_tax: false,
    exclude_from_cm_rate: false,
    exclude_flash_pay_at_property: false,
  };
}

export function withDefaultTaxFeeProductType<T extends RJSFSchema>(schema: T): T {
  const items = schema.items;

  if (!items || typeof items !== 'object' || Array.isArray(items)) {
    return schema;
  }

  const productType = items.properties?.product_type;

  if (!productType || typeof productType !== 'object' || Array.isArray(productType)) {
    return schema;
  }

  return {
    ...schema,
    items: {
      ...items,
      properties: {
        ...items.properties,
        product_type: {
          ...productType,
          default: DEFAULT_FLASH_TAX_FEE_PRODUCT_TYPE,
        },
      },
    },
  };
}

export function getTaxFeePrefillItems(country?: string | null, city?: string | null) {
  const normalizedCountry = normalize(country);
  const normalizedCity = normalize(city);

  if (!normalizedCountry && !normalizedCity) {
    return [];
  }

  if (normalizedCountry === 'thailand' && normalizedCity.includes('koh samui')) {
    return KOH_SAMUI_RULES.map(toTaxFeeFormItem);
  }

  const matchingCity = Object.keys(TAX_RULES_BY_CITY).find(
    (cityKey) =>
      normalizedCity.includes(cityKey) && (!normalizedCountry || normalizedCountry === CITY_RULE_COUNTRY[cityKey]),
  );

  if (matchingCity) {
    return TAX_RULES_BY_CITY[matchingCity].map(toTaxFeeFormItem);
  }

  const matchingCountry = Object.keys(TAX_RULES_BY_COUNTRY).find((countryKey) => normalizedCountry === countryKey);

  if (matchingCountry) {
    return TAX_RULES_BY_COUNTRY[matchingCountry].map(toTaxFeeFormItem);
  }

  return [];
}

const formatTaxFeeRate = (item: TaxFeeFormItem) =>
  typeof item.unit.amount === 'number'
    ? `${item.unit.amount} ${item.unit.currency ?? ''}`.trim()
    : `${item.unit.value ?? 0}%`;

export const taxFeeKey = (item: TaxFeeFormItem) => `${item.name} — ${formatTaxFeeRate(item)}`;

const normalizeTaxFeeName = (name: string) => name.trim().toLowerCase();

export function mergeTaxFeeItems(existingItems: Array<TaxFeeFormItem>, prefillItems: Array<TaxFeeFormItem>) {
  if (!prefillItems.length) {
    return { added: [], changed: [], items: existingItems, removed: [] };
  }

  const added: Array<string> = [];
  const changed: Array<string> = [];
  const removed: Array<string> = [];
  const matchedNames = new Set<string>();

  const items = existingItems.flatMap((item) => {
    const rule = prefillItems.find((prefill) => normalizeTaxFeeName(prefill.name) === normalizeTaxFeeName(item.name));

    if (!rule) {
      removed.push(taxFeeKey(item));
      return [];
    }

    matchedNames.add(normalizeTaxFeeName(rule.name));

    if (formatTaxFeeRate(rule) === formatTaxFeeRate(item) && item.name === rule.name) {
      return [item];
    }

    changed.push(`${taxFeeKey(item)} → ${taxFeeKey(rule)}`);

    // Keep the property's own settings (payable at property, CM flags); only the name/rate follow the rules.
    return [{ ...item, name: rule.name, unit: rule.unit }];
  });

  const addedItems = prefillItems.filter((rule) => !matchedNames.has(normalizeTaxFeeName(rule.name)));

  addedItems.forEach((rule) => added.push(taxFeeKey(rule)));

  return { added, changed, items: [...items, ...addedItems], removed };
}
